import { Cta } from "@/components/Cta";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { TerminalCard } from "@/components/TerminalCard";
import { useSeo } from "@/lib/seo";
import { Card, CodeBlock, Container, Grid, SimpleGrid, Stack, Text, Title } from "@repo/ui/core";
import {
  IconArrowRight,
  IconBolt,
  IconCode,
  IconDeviceDesktop,
  IconGitBranch,
  IconStack2,
  IconTerminal,
} from "@repo/ui/icons";
import styles from "@styles/site.module.css";

const devFeatures = [
  {
    icon: IconBolt,
    title: "Fast Builds",
    desc: "Snapshot restores and burst compute cut full AAOSP builds from hours to minutes.",
  },
  {
    icon: IconDeviceDesktop,
    title: "Cloud Emulators",
    desc: "Connect to emulators from your browser. Multi-display, GPS simulation, real-time debugging.",
  },
  {
    icon: IconCode,
    title: "Browser IDE",
    desc: "Full VS Code in the browser via GitLab Workspaces. Your extensions, your settings, zero setup.",
  },
  {
    icon: IconTerminal,
    title: "Real Debugging",
    desc: "Device logs, ADB commands, VHAL properties, all accessible directly in the browser.",
  },
  {
    icon: IconGitBranch,
    title: "GitLab Native",
    desc: "CI templates, workspaces, registries. Works with your existing GitLab workflow.",
  },
  {
    icon: IconStack2,
    title: "Custom Environments",
    desc: "Devfiles per repo or per user. SSH access when you need it.",
  },
];

export default function Developers() {
  useSeo("/solutions/developers");

  return (
    <>
      <PageHero
        eyebrow="For Developers"
        title={
          <>
            Ship infotainment code without the{" "}
            <span className={styles.gradientText}>bench</span>
          </>
        }
        description="Fast builds, cloud emulators, and a full IDE in the browser. Everything integrates with your existing GitLab workflow, so you focus on code, not infrastructure."
      >
        <Cta to="/contact">Book a Demo</Cta>
        <Cta to="/platform" variant="ghost" iconRight={<IconArrowRight size={16} />}>
          Explore Platform
        </Cta>
      </PageHero>

      {/* Feature grid */}
      <Section>
        <SectionHeader
          title="Purpose-built tools for automotive development"
          description="Build, emulate, debug, and test without leaving your browser."
        />
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {devFeatures.map((feat, i) => (
            <Reveal key={feat.title} delay={(i % 3) * 0.05}>
              <Card padding="xl" className={styles.card} style={{ height: "100%" }}>
                <Stack gap="md">
                  <span className={styles.iconChip}>
                    <feat.icon size={20} stroke={1.5} />
                  </span>
                  <Title order={3} size="1.125rem" fw={600}>
                    {feat.title}
                  </Title>
                  <Text c="dimmed" size="sm" lh={1.7}>
                    {feat.desc}
                  </Text>
                </Stack>
              </Card>
            </Reveal>
          ))}
        </SimpleGrid>
      </Section>

      {/* Quick start */}
      <Section hairline>
        <Grid gutter={64} align="center">
          <Grid.Col span={{ base: 12, md: 5 }}>
            <SectionHeader
              title="Get started in minutes"
              description="Add our CI templates to your .gitlab-ci.yml, configure your build target, and push. That's it."
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Reveal delay={0.08} y={24}>
              <TerminalCard title=".gitlab-ci.yml">
                <CodeBlock
                  code={`# Add the Null Autos CI templates
include:
  - remote: 'https://null.autos/ci/android.yml'
  - remote: 'https://null.autos/ci/emulator.yml'

# Configure your build target
build:
  extends: .android-build
  variables:
    TARGET: automotive_arm64

# Push and watch it build
# $ git push origin main`}
                  language="yaml"
                  withCopyButton={false}
                  style={{ fontSize: "0.8125rem", background: "transparent" }}
                  styles={{
                    codeHighlight: { "--ch-background": "transparent" },
                    pre: { padding: 0, margin: 0, lineHeight: 1.8 },
                  }}
                />
              </TerminalCard>
            </Reveal>
          </Grid.Col>
        </Grid>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title order={2} fw={650} size="2rem" style={{ letterSpacing: "-0.02em" }}>
              Push code, watch it run
            </Title>
            <Text size="lg" c="dimmed" maw={480} mx="auto" mt="md" mb="xl" lh={1.65}>
              Bring a repo. We will show you the full pipeline on your own code.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
